import { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Select,
  Button,
  Group,
  Text,
  Table,
  ScrollArea,
  Stepper,
  Box,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Participant } from '../../../types';
import { participantsApi } from '../../../services/api/participants';

export type CustomFieldType = 'text' | 'number' | 'date';

export interface AddCustomColumnModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  participants: Participant[];
  onFieldCreated: () => void;
}

interface ParticipantFieldValue {
  participantId: string;
  participantName: string;
  value: string;
}

export function AddCustomColumnModal({
  opened,
  onClose,
  cohortId,
  participants,
  onFieldCreated,
}: AddCustomColumnModalProps) {
  const [step, setStep] = useState(0);
  const [fieldName, setFieldName] = useState('');
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>('text');
  const [fieldValues, setFieldValues] = useState<ParticipantFieldValue[]>([]);
  const [saving, setSaving] = useState(false);
  const [fieldNameError, setFieldNameError] = useState('');

  // Reset form when modal closes
  const handleClose = () => {
    setStep(0);
    setFieldName('');
    setFieldLabel('');
    setFieldType('text');
    setFieldValues([]);
    setFieldNameError('');
    onClose();
  };

  // Generate field name from label
  const generateFieldName = (label: string) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  };

  // Handle label change and auto-generate field name
  const handleLabelChange = (value: string) => {
    setFieldLabel(value);
    if (!fieldName || fieldName === generateFieldName(fieldLabel)) {
      setFieldName(generateFieldName(value));
    }
    setFieldNameError('');
  };

  // Move to step 2 - populate participant values
  const handleNext = () => {
    if (!fieldLabel.trim()) {
      setFieldNameError('Field label is required');
      return;
    }
    if (!fieldName.trim()) {
      setFieldNameError('Field name is required');
      return;
    }

    // Initialize values for all participants
    const initialValues = participants.map((p) => ({
      participantId: p.id,
      participantName: `${p.first_name} ${p.last_name}`,
      value: '',
    }));
    setFieldValues(initialValues);
    setStep(1);
  };

  // Update a participant's field value
  const handleValueChange = useCallback((participantId: string, value: string) => {
    setFieldValues((prev) =>
      prev.map((pv) => (pv.participantId === participantId ? { ...pv, value } : pv))
    );
  }, []);

  // Save the custom field
  const handleSave = async () => {
    setSaving(true);
    try {
      // Create the custom field definition
      await participantsApi.createCustomField(cohortId, {
        field_name: fieldName,
        field_label: fieldLabel,
        field_type: fieldType,
      });

      // Bulk update values for participants that have values
      const valuesWithData = fieldValues.filter((v) => v.value.trim());
      if (valuesWithData.length > 0) {
        const values = valuesWithData.map((v) => ({
          participantId: v.participantId,
          value: v.value.trim(),
        }));
        await participantsApi.bulkUpdateCustomFieldValues(cohortId, fieldName, values);
      }

      notifications.show({
        title: 'Custom Column Created',
        message: `"${fieldLabel}" has been added as a custom column`,
        color: 'green',
      });

      onFieldCreated();
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create custom column',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // Skip value entry and just create the field
  const handleSkipValues = async () => {
    setSaving(true);
    try {
      await participantsApi.createCustomField(cohortId, {
        field_name: fieldName,
        field_label: fieldLabel,
        field_type: fieldType,
      });

      notifications.show({
        title: 'Custom Column Created',
        message: `"${fieldLabel}" has been added as a custom column`,
        color: 'green',
      });

      onFieldCreated();
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create custom column',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const getInputType = () => {
    switch (fieldType) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Custom Column"
      size="lg"
    >
      <Stepper active={step} size="sm" mb="lg">
        <Stepper.Step label="Define Column" description="Name and type" />
        <Stepper.Step label="Enter Values" description="Optional" />
      </Stepper>

      {step === 0 && (
        <Stack gap="md">
          <TextInput
            label="Column Label"
            placeholder="e.g., Employee ID"
            value={fieldLabel}
            onChange={(e) => handleLabelChange(e.currentTarget.value)}
            required
            error={fieldNameError && !fieldLabel.trim() ? fieldNameError : undefined}
          />
          <TextInput
            label="Field Name"
            placeholder="e.g., employee_id"
            description="Used internally for data storage"
            value={fieldName}
            onChange={(e) => {
              setFieldName(e.currentTarget.value);
              setFieldNameError('');
            }}
            required
            error={fieldNameError && fieldLabel.trim() && !fieldName.trim() ? fieldNameError : undefined}
          />
          <Select
            label="Field Type"
            value={fieldType}
            onChange={(value) => setFieldType((value || 'text') as CustomFieldType)}
            data={[
              { value: 'text', label: 'Text' },
              { value: 'number', label: 'Number' },
              { value: 'date', label: 'Date' },
            ]}
            required
          />
          <Group justify="flex-end" className="modal-footer">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleNext}>
              Next
            </Button>
          </Group>
        </Stack>
      )}

      {step === 1 && (
        <Stack gap="md">
          <Box>
            <Text size="sm" c="dimmed" mb="xs">
              Optionally enter values for "{fieldLabel}" for each participant.
              You can also skip this step and add values later.
            </Text>
          </Box>
          <ScrollArea.Autosize mah={400}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Participant</Table.Th>
                  <Table.Th>{fieldLabel}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {fieldValues.map((pv) => (
                  <Table.Tr key={pv.participantId}>
                    <Table.Td>
                      <Text size="sm">{pv.participantName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        type={getInputType()}
                        value={pv.value}
                        onChange={(e) =>
                          handleValueChange(pv.participantId, e.currentTarget.value)
                        }
                        placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                        size="xs"
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={() => setStep(0)}>
              Back
            </Button>
            <Group gap="sm">
              <Button variant="default" onClick={handleSkipValues} loading={saving}>
                Skip & Create
              </Button>
              <Button onClick={handleSave} loading={saving}>
                Save with Values
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default AddCustomColumnModal;
